import 'whatwg-fetch';

import { isJSON } from '@v4/common/utils/string';
import { parseUrl } from 'query-string';
import { stringifyUrl } from '@v4/common/utils/url';
import { isDevelopment } from '../environment';

/**
 * Checks whether or not the response has a status code of 401
 *
 * @param {Object} response
 * @returns {Boolean}
 */
export const isUnAuthorized = (response) => response.status === 401;

/**
 * Checks whether or not the response has a status code of 404
 *
 * @param {Object} response
 * @returns {Boolean}
 */
export const isNotFound = (response) => response.status === 404;

/**
 * Replace the window location with a specific URL
 *
 * @param {String} url
 * @returns {void}
 */
export const redirect = (url) => window.location.replace(url);

/**
 * Changes the window location to a specific URL
 *
 * @param {String} url
 * @returns {void}
 */
export const navigateTo = (url) => window.location.assign(url);

/**
 * Reloads the current page
 *
 * @returns {void}
 */
export const reloadPage = () => window.location.reload();

/**
 * Transforms a JSON string response to JSON
 *
 * @param {String} response
 * @returns {Promise<Object>}
 */
export const toJson = async (response) => await response.json();

/**
 * Checks if the status code falls in the range of 2xx
 *
 * @param {number} status - HTTP status code
 * @returns {Boolean}
 */
export const isSuccessfulStatus = (status) => typeof status === 'number' && status >= 200 && status <= 299;

/**
 * Fetching a resource from the network
 *
 * @param {String} url resource that you wish to fetch
 * @param {Object} init options
 * @deprecated use `request` method instead
 *
 * @returns {Promise<*>} response
 */
export const doRequest = async (url, init = {}) => {
    const config = {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json; charset=UTF-8',
            ...(init.headers || {}),
        },
        ...init,
    };
    config.headers = { 'Content-Type': 'application/json; charset=UTF-8', ...(init.headers || {}) };

    const response = await fetch(url, config);

    if (!response.ok) {
        const error = new Error(response.statusText);
        error.response = response;
        try {
            error.body = await response.json();
        } catch (e) {
            error.body = null;
        }

        throw error;
    }

    return response;
};

/**
 * Fetch resources asynchronously across the network
 *
 * @param {String} url resource that you wish to fetch
 * @param {Object} init fetch options
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/WindowOrWorkerGlobalScope/fetch}
 *
 * @returns {Promise<*>} response
 */
export const request = (url, opts) => {
    const options = {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json; charset=UTF-8',
        },
        ...opts,
    };

    // convert body to a JSON string
    if (
        options.body &&
        typeof options.body !== 'string' &&
        !(options.body instanceof FormData) &&
        isJSON(options.body)
    ) {
        options.body = JSON.stringify(options.body);
    }

    return new Promise((resolve, reject) =>
        fetch(url, options)
            .then((response) =>
                toJson(response)
                    .then((data) => {
                        const responseSchema = {
                            // `data` is the response that was provided by the server
                            data,
                            // `status` is the HTTP status code from the server response
                            status: response.status,
                            // `statusText` is the HTTP status message from the server response
                            statusText: response.statusText,
                            // `headers` the headers that the server responded with
                            headers: response.headers,
                        };

                        // status code that falls out of the range of `2xx`
                        if (!response.ok || !isSuccessfulStatus(response.status)) {
                            return reject({ response: responseSchema, request: options });
                        }

                        return resolve({ ...responseSchema, request: options });
                    })
                    .catch((error) => reject({ error }))
            )
            .catch((error) => reject({ error }))
    );
};

/**
 * Use XMLHttpRequest object to interact with servers.
 *
 * @param {object} options
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest|XMLHttpRequest}
 *
 * @returns {Promise<object>} response
 */
export const httpRequest = (options = {}) =>
    new Promise((resolve, reject) => {
        const config = {
            data: null,
            method: 'GET',
            async: true,
            timeout: 0, // there is no timeout
            cache: true,
            ...options,
        };

        if (config.cache === false && config.method === 'GET') {
            const { url, query: currentQuery } = parseUrl(config.url);

            /**
             * @see {@link https://gist.github.com/gordonbrander/2230317|Unique ID}
             */
            const query = {
                ...currentQuery,
                timestamp: Date.now().toString(36) + Math.random().toString(36).substr(2, 5),
            };

            config.url = stringifyUrl({ url, query });
        }

        const request = new XMLHttpRequest();
        request.timeout = isDevelopment() ? 0 : config.timeout; // Set timeout 0 (infinite/no timeout) for development
        request.onerror = (event) => reject({ error: { event } }); // connection error has occurred, e.g. wrong domain name
        request.onabort = (event) => reject({ error: { event } }); // the request was canceled by the call `request.abort()`
        request.ontimeout = (event) => reject({ error: { event } }); // the request was canceled due to timeout (only happens if it was set)

        request.onreadystatechange = () => {
            const headers = request.getAllResponseHeaders();

            // when `headers` exists means that the request was not aborted
            if (headers && request.readyState === request.DONE) {
                let data;

                try {
                    data = JSON.parse(request.responseText);
                } catch (e) {
                    data = {};
                }

                // create a map of header names to values
                const headerMap = {};
                headers
                    .trim()
                    .split(/[\r\n]+/)
                    .forEach((line) => {
                        const parts = line.split(': ');
                        const header = parts.shift();
                        const value = parts.join(': ');
                        headerMap[header] = value;
                    });

                const responseSchema = {
                    // `data` is the response that was provided by the server
                    data,
                    // `status` is the HTTP status code from the server response
                    status: request.status,
                    // `statusText` is the HTTP status message from the server response
                    statusText: request.statusText,
                    // `headers` the headers that the server responded with
                    headers: headerMap,
                    // `config` is the config that was provided to the request
                    config,
                    // `request` is the request that generated this response
                    request,
                };

                // status code that falls out of the range of `2xx`
                if (!isSuccessfulStatus(request.status)) {
                    return reject({ response: responseSchema });
                }

                // status code `2xx`
                return resolve(responseSchema);
            }
        };

        request.open(config.method.toUpperCase(), config.url, config.async);
        const headers = {
            'Content-type': 'application/json',
            ...(config.headers || {}),
        };
        Object.entries(headers).forEach(([k, v]) => {
            request.setRequestHeader(k, v);
        });
        request.send(config.method.toUpperCase() === 'GET' ? null : JSON.stringify(config.data));
    });
